import { FILE_TYPE, IconAccordingToType } from "../modules/task-management/models/file-upload.model";
import { saveAs } from "file-saver";
import { CustomDropdownOption } from "../modules/shared/Models/shared-data-models.model";
import {
    FileUploadValidationResponseV2,
    FileValidationConfig,
} from "../modules/conversation/models/conversation.model";
import { FileValidationError, FileValidationErrorType } from "../modules/conversation/models/message.model";

export function getFileExtension(fileName: string | null | undefined): string {
    if (!fileName) {
        return "";
    }

    let regex = new RegExp("[^.]+$");
    return String(regex.exec(fileName));
}

export function getFileTypeFromExtension(extension: string): string {
    switch (extension.toLowerCase()) {
        case "jpeg":
        case "jpg":
        case "gif":
        case "png":
        case "webp":
        case "svg":
        case "bmp":
            return FILE_TYPE.IMAGE;
        case "aac":
        case "ogg":
        case "wav":
        case "mp3":
        case "flac":
        case "m4a":
        case "m4b":
        case "mpc":
        case "opus":
        case "awb":
        case "alac":
        case "amr":
            return FILE_TYPE.AUDIO;
        case "wmv":
        case "mkv":
        case "mp4":
        case "avi":
        case "webm":
            return FILE_TYPE.VIDEO;
        case "pdf":
            return FILE_TYPE.PDF;
        case "xlsx":
        case "csv":
        case "xlsb":
        case "xlsm":
        case "xls":
            return FILE_TYPE.EXCEL;
        case "doc":
        case "docx":
        case "rtf":
            return FILE_TYPE.WORD;
        case "ppt":
        case "pptx":
        case "pptm":
            return FILE_TYPE.POWERPOINT;
        case "txt":
            return FILE_TYPE.TEXT;
        case "pbix":
        case "pbit":
        case "pdids":
        case "rdl":
        case "json":
        case "twb":
        case "twbx":
            return FILE_TYPE.OTHER;
        default:
            return FILE_TYPE.OTHER;
    }
}

export function getFileType(fileName: string | null | undefined): string {
    if (!fileName) {
        return "";
    }
    let extension = getFileExtension(fileName);
    return getFileTypeFromExtension(extension);
}

export function getFileSize(size: number | null | undefined): string {
    if (!size) {
        return "";
    }
    let sizes = [" Bytes", " KB", " MB", " GB", " TB", " PB", " EB", " ZB", " YB"];
    for (let i = 1; i < sizes.length; i++) {
        if (size < Math.pow(1024, i)) {
            return Math.round((size / Math.pow(1024, i - 1)) * 100) / 100 + sizes[i - 1];
        }
    }
    return size.toString();
}

export function getFilePreviewIconSrc(fileType: string): string {
    return IconAccordingToType[fileType as keyof typeof IconAccordingToType];
}

export function saveFileInUserSystem(data: Blob, fileName: string) {
    let blob = new Blob([data], { type: "application/octet-stream" });
    saveAs(blob, `${fileName}`);
}

export function filterHtmlTags(htmlString: string): string {
    let div = document.createElement("div");
    div.innerHTML = htmlString;
    return div.innerText ?? "";
}

export function formatSelectOptions(data: any[], valueKey: string, labelKey: string): CustomDropdownOption[] {
    return data.map((item) => ({ value: item[valueKey], label: item[labelKey] })) as CustomDropdownOption[];
}

export function formatSelectOptionsSprint(data: any[], valueKey: string, labelKey: string): CustomDropdownOption[] {
    const formatDate = (dateString: string | null) => {
        if (!dateString) {
            return "Invalid date";
        }
        const date = new Date(dateString);
        const options: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short", year: "numeric" };
        return new Intl.DateTimeFormat("en-GB", options).format(date).replace(/ /g, "");
    };

    return data.map((item) => {
        const value = item[valueKey] as string;
        const title = item[labelKey] as string;
        const startDate = formatDate(item.sprintExpStartDate);
        const endDate = formatDate(item.sprintExpEndDate);

        return {
            value,
            label: `${title}`,
            labelExtended: `(${startDate} - ${endDate})`,
        };
    }) as CustomDropdownOption[];
}
export function formatSelectOptionsEpic(
    data: any[],
    valueKey: string,
    labelKey: string
): CustomDropdownOption[] {
    const options = data?.map((item) => {
        const value = item[valueKey] ?? 'Unknown Value';
        const title = item[labelKey] ?? 'Unknown Title';
        const epicNumber = item?.epicNumber ?? 'Unknown Epic';

        const createdDate = item?.fkworkFlowEpicStatus?.createdDateTime
            ? new Date(item.fkworkFlowEpicStatus.createdDateTime)
            : null;

        const actualEndDate = item?.actualEndDateTime
            ? new Date(item.actualEndDateTime)
            : null;

        return {
            value,
            label: title,
            labelExtended: epicNumber,
            extras: {
                createdDate,
                actualEndDate
            }
        };
    }) as CustomDropdownOption[];

    return options?.sort((a, b) => {
        // sort by epic title
        const labelCompare = a.label.localeCompare(b.label, undefined, {
            sensitivity: 'base'
        });
        if (labelCompare !== 0) {
            return labelCompare;
        }

        // sort by created date
        const createdA = a.extras?.createdDate?.getTime() ?? 0;
        const createdB = b.extras?.createdDate?.getTime() ?? 0;

        if (createdA !== createdB) {
            return createdA - createdB; // older first
        }

        // sort by actual end date
        const endA = a.extras?.actualEndDate?.getTime() ?? 0;
        const endB = b.extras?.actualEndDate?.getTime() ?? 0;

        return endA - endB; // older first
    }) ?? [];
}



export function validateUploadAttachmentRequestFiles(
    requestedFiles: File[],
    existingFiles: File[] = [],
    config: Partial<FileValidationConfig> = {},
    createCommonErrorMessage: boolean = true,
): FileUploadValidationResponseV2 {
    // Merge provided config with defaults
    const validationConfig: FileValidationConfig = {
        ...config,
    } as FileValidationConfig;

    // Initialize response structure
    const response: FileUploadValidationResponseV2 = {
        isValid: false,
        validFiles: [],
        invalidFiles: [],
        summary: {
            totalRequested: requestedFiles.length,
            validCount: 0,
            invalidCount: 0,
            skippedDueToLimit: 0,
            wouldExceedLimit: false,
        },
        errors: {
            unsupportedTypeFiles: [],
            filesExceedingSize: [],
            filesWithLongNames: [],
            duplicateNameFiles: [],
            emptyFiles: [],
            filesWithoutExtension: [],
            filesWithInvalidChars: [],
            filesExceedingCountLimit: [],
        },
        combineErrorMessage: "",
    };

    // Early validation checks
    if (!requestedFiles || requestedFiles.length === 0) {
        return response;
    }

    // Check if adding these files would exceed the total limit
    const currentFileCount = existingFiles.length;
    response.summary.wouldExceedLimit = currentFileCount + requestedFiles.length > validationConfig.maxFilesAllowed;

    // Get existing file names for duplicate checking
    const existingFileNames = getExistingFileNames(existingFiles, validationConfig.caseSensitiveDuplicateCheck);

    // Track processed file names to prevent duplicates within the current batch
    const processedFileNames = new Set<string>();

    // Validate each file
    requestedFiles.forEach((file, index) => {
        const validationErrors = validateSingleFile(
            file,
            existingFileNames,
            processedFileNames,
            validationConfig,
            currentFileCount + response.validFiles.length,
        );

        if (validationErrors.length === 0) {
            // Check if we haven't exceeded the limit
            if (currentFileCount + response.validFiles.length < validationConfig.maxFilesAllowed) {
                response.validFiles.push(file);
                const fileName = validationConfig.caseSensitiveDuplicateCheck ? file.name : file.name.toLowerCase();
                processedFileNames.add(fileName);
            } else {
                response.summary.skippedDueToLimit++;
                const limitError: FileValidationError = {
                    file,
                    errorType: FileValidationErrorType.EXCEEDING_COUNT_LIMIT,
                    errorMessage: `File "${file.name}" cannot be uploaded as it would exceed the maximum allowed file count of ${validationConfig.maxFilesAllowed}`,
                };
                response.invalidFiles.push(limitError);
                categorizeError(limitError, response.errors);
            }
        } else {
            // Categorize errors
            validationErrors.forEach((error) => {
                response.invalidFiles.push(error);
                categorizeError(error, response.errors);
            });
        }
    });

    // If we have errors, create a combined error message
    if (createCommonErrorMessage && response.invalidFiles.length > 0) {
        const errorMessages = response.invalidFiles.map((error) => error.errorMessage).join(" | ");
        const fileCountMessage = response.invalidFiles.length > 1 ? "files" : "file";
        response.combineErrorMessage = `Validation failed for ${response.invalidFiles.length } ${fileCountMessage} : ${errorMessages}`;
    }

    // Update summary
    response.summary.validCount = response.validFiles.length;
    response.summary.invalidCount = response.invalidFiles.length;
    response.isValid = response.validFiles.length > 0; // At least one valid file
    return response;
}

// HELPER FUNCTIONS FOR ATTACHMENT VALIDATION

/**
 * Validates a single file against all criteria
 */
function validateSingleFile(
    file: File,
    existingFileNames: Set<string>,
    processedFileNames: Set<string>,
    config: FileValidationConfig,
    currentValidFileCount: number,
): FileValidationError[] {
    const errors: FileValidationError[] = [];
    // 1. Check for empty files
    if (file.size === 0) {
        errors.push({
            file,
            errorType: FileValidationErrorType.EMPTY_FILE,
            errorMessage: `File "${file.name}" is empty (0 bytes)`,
        });
    }

    // 2. Check file size
    if (file.size > config.maxFileSizeBytes) {
        errors.push({
            file,
            errorType: FileValidationErrorType.FILE_TOO_LARGE,
            errorMessage: `File "${file.name}" (${formatFileSize(file.size)}) exceeds maximum size of ${formatFileSize(
                config.maxFileSizeBytes,
            )}`,
        });
    }

    // 3. Check filename length
    if (file.name.length > config.maxFileNameLength) {
        errors.push({
            file,
            errorType: FileValidationErrorType.FILENAME_TOO_LONG,
            errorMessage: `Filename "${file.name}" exceeds maximum length of ${config.maxFileNameLength} characters`,
        });
    }

    // 4. Check for invalid characters in filename
    if (hasInvalidFileNameCharacters(file.name)) {
        errors.push({
            file,
            errorType: FileValidationErrorType.INVALID_CHARACTERS,
            errorMessage: `Filename "${file.name}" contains invalid characters. Avoid: < > : " | ? * \\ /`,
        });
    }

    // 5. Extract and validate file extension
    const fileExtension = extractFileExtension(file.name);
    if (!fileExtension) {
        errors.push({
            file,
            errorType: FileValidationErrorType.NO_EXTENSION,
            errorMessage: `File "${file.name}" has no file extension`,
        });
    }

    // 6. Check file type (both extension and MIME type)
    if (fileExtension && !isFileTypeAllowed(fileExtension, file.type, config)) {
        errors.push({
            file,
            errorType: FileValidationErrorType.UNSUPPORTED_TYPE,
            errorMessage: `File type "${fileExtension}" or MIME type "${file.type}" is not supported`,
        });
    }

    // 7. Check for duplicate names (if enabled)
    if (config.checkDuplicateNames) {
        const fileName = config.caseSensitiveDuplicateCheck ? file.name : file.name.toLowerCase();

        if (existingFileNames.has(fileName) || processedFileNames.has(fileName)) {
            errors.push({
                file,
                errorType: FileValidationErrorType.DUPLICATE_NAME,
                errorMessage: `File with name "${file.name}" already exists`,
            });
        }
    }
    return errors;
}

/**
 * Extracts file extension from filename (for conversation attachments only)
 */
export function extractFileExtension(filename: string): string {
    const lastDotIndex = filename.lastIndexOf(".");
    if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
        return "";
    }
    return filename.substring(lastDotIndex).toLowerCase();
}

/**
 * Checks if file type is allowed based on extension and MIME type
 */
function isFileTypeAllowed(extension: string, mimeType: string, config: FileValidationConfig): boolean {
    const extensionAllowed = config.allowedFileTypes.some((type) => type.toLowerCase() === extension.toLowerCase());

    const mimeTypeAllowed = config.allowedMimeTypes.some((type) => type.toLowerCase() === mimeType.toLowerCase());

    // File is allowed if either extension OR MIME type matches (more flexible)
    return extensionAllowed || mimeTypeAllowed;
}

/**
 * Checks for invalid characters in filename
 */
function hasInvalidFileNameCharacters(filename: string): boolean {
    const invalidChars = /[<>:"|?*\\/]/;
    return invalidChars.test(filename);
}

/**
 * Gets existing file names as a Set for efficient lookup
 */
function getExistingFileNames(existingFiles: File[], caseSensitive: boolean): Set<string> {
    return new Set(existingFiles.map((file) => (caseSensitive ? file.name : file.name.toLowerCase())));
}

/**
 * Categorizes validation errors into response structure
 */
function categorizeError(error: FileValidationError, errors: FileUploadValidationResponseV2["errors"]): void {
    switch (error.errorType) {
        case FileValidationErrorType.UNSUPPORTED_TYPE:
            errors.unsupportedTypeFiles.push(error);
            break;
        case FileValidationErrorType.FILE_TOO_LARGE:
            errors.filesExceedingSize.push(error);
            break;
        case FileValidationErrorType.FILENAME_TOO_LONG:
            errors.filesWithLongNames.push(error);
            break;
        case FileValidationErrorType.DUPLICATE_NAME:
            errors.duplicateNameFiles.push(error);
            break;
        case FileValidationErrorType.EMPTY_FILE:
            errors.emptyFiles.push(error);
            break;
        case FileValidationErrorType.NO_EXTENSION:
            errors.filesWithoutExtension.push(error);
            break;
        case FileValidationErrorType.INVALID_CHARACTERS:
            errors.filesWithInvalidChars.push(error);
            break;
        case FileValidationErrorType.EXCEEDING_COUNT_LIMIT:
            errors.filesExceedingCountLimit.push(error);
            break;
    }
}

/**
 * Formats file size for human-readable display
 */
export function formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}
